import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import Papa from 'papaparse';
import styles from './Schools.module.css';
import 'leaflet/dist/leaflet.css';

// Helper function to get color based on percentile (relative to displayed schools)
const getPercentileColor = (percentile, minPercentile = 0, maxPercentile = 100) => {
  if (percentile === null || percentile === undefined || isNaN(percentile)) {
    return '#555';
  }
  
  // Normalize percentile to 0-100 based on the range of displayed schools
  const range = maxPercentile - minPercentile;
  const normalizedPercentile = range > 0 ? ((percentile - minPercentile) / range) * 100 : 50;
  const clampedPercentile = Math.max(0, Math.min(100, normalizedPercentile));
  
  // Interpolate hue from red (0) to green (120)
  const hue = (clampedPercentile / 100) * 120;
  return `hsl(${hue}, 70%, 50%)`;
};

// Helper function to calculate distance between two points (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Radius of Earth in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Component to handle map clicks and show popup immediately
const MapClickHandler = ({ onMapClick, tempMarker, onConfirm, onCancel }) => {
  const map = useMap();
  
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    }
  });
  
  // Show popup immediately when tempMarker is set
  useEffect(() => {
    if (tempMarker) {
      const popup = L.popup({
        closeButton: false,
        autoClose: false,
        closeOnEscapeKey: false,
        className: 'temp-location-popup'
      })
      .setLatLng([tempMarker.latitude, tempMarker.longitude])
      .setContent(`
        <div class="popup-content">
          <h4>Set Search Location</h4>
          <p>Search for schools near this location?</p>
          <div class="popup-buttons">
            <button class="confirm-button" onclick="window.confirmLocation()">Set Location Here</button>
            <button class="cancel-button" onclick="window.cancelLocation()">Cancel</button>
          </div>
        </div>
      `)
      .openOn(map);
      
      // Attach event handlers to window
      window.confirmLocation = onConfirm;
      window.cancelLocation = onCancel;
      
      return () => {
        window.confirmLocation = null;
        window.cancelLocation = null;
        map.closePopup(popup);
      };
    }
  }, [tempMarker, map, onConfirm, onCancel]);
  
  return null;
};

// Component to handle map centering from external triggers
const MapController = ({ centerLocation, onCenterComplete }) => {
  const map = useMap();
  
  useEffect(() => {
    if (centerLocation) {
      map.setView([centerLocation.latitude, centerLocation.longitude], 12, {
        animate: true,
        duration: 1
      });
      // Call the completion callback after animation
      setTimeout(() => {
        if (onCenterComplete) onCenterComplete();
      }, 1000);
    }
  }, [centerLocation, map, onCenterComplete]);
  
  return null;
};

// Component to update map bounds
const MapBoundsUpdater = ({ schools, searchType, shouldUpdateBounds = true }) => {
  const map = useMap();
  
  useEffect(() => {
    // Only update bounds if we should (not during map click searches) and we have schools
    if (shouldUpdateBounds && schools && schools.length > 0) {
      const bounds = schools.map(school => [school.latitude, school.longitude]);
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [schools, map, shouldUpdateBounds]);
  
  return null;
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Schools = () => {
  // State for data and loading
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtering, setFiltering] = useState(false);
  
  // Filter states
  const [schoolType, setSchoolType] = useState('all'); // 'all', 'primary', 'secondary'
  const [minPercentile, setMinPercentile] = useState('');
  const [numSchoolsToShow, setNumSchoolsToShow] = useState('200');
  const [postcode, setPostcode] = useState('');
  
  // Debounced filter values to improve performance
  const debouncedMinPercentile = useDebounce(minPercentile, 300);
  const debouncedNumSchoolsToShow = useDebounce(numSchoolsToShow, 300);
  const debouncedSchoolType = useDebounce(schoolType, 100);
  
  // Search state
  const [searchLocation, setSearchLocation] = useState(null);
  const [searchType, setSearchType] = useState(null); // 'postcode' or 'map'
  const [mapClickLocation, setMapClickLocation] = useState(null);
  const [tempMarker, setTempMarker] = useState(null); // Temporary marker before confirmation
  const [isSearching, setIsSearching] = useState(false);
  
  // Map control state
  const [mapCenterLocation, setMapCenterLocation] = useState(null);
  
  // Load CSV data on component mount
  useEffect(() => {
    const loadSchoolsData = async () => {
      try {
        setLoading(true);
        const response = await fetch('/schools_combined.csv');
        if (!response.ok) {
          throw new Error('Failed to load schools data');
        }
        const csvText = await response.text();
        
        Papa.parse(csvText, {
          header: true,
          complete: (results) => {
            const processedData = results.data
              .filter(row => row.school_name && row.latitude && row.longitude)
              .map(row => ({
                school_name: row.school_name,
                percentile: parseFloat(row.percentile),
                latitude: parseFloat(row.latitude),
                longitude: parseFloat(row.longitude),
                postcode: row.postcode,
                school_type: row.school_type
              }));
            
            setSchools(processedData);
            setLoading(false);
          },
          error: (error) => {
            setError('Error parsing CSV data: ' + error.message);
            setLoading(false);
          }
        });
      } catch (err) {
        setError('Error loading schools data: ' + err.message);
        setLoading(false);
      }
    };
    
    loadSchoolsData();
  }, []);
  
  // Filter schools based on current filters
  const filteredSchools = useMemo(() => {
    if (schools.length === 0) return [];
    
    setFiltering(true);
    
    const filtered = schools.filter(school => {
      // Filter by school type
      if (debouncedSchoolType !== 'all' && school.school_type !== debouncedSchoolType) {
        return false;
      }
      
      // Filter by percentile range
      if (debouncedMinPercentile && school.percentile < parseFloat(debouncedMinPercentile)) {
        return false;
      }
      
      return true;
    });
    
    // Use setTimeout to update filtering state after render
    setTimeout(() => setFiltering(false), 0);
    
    return filtered;
  }, [schools, debouncedSchoolType, debouncedMinPercentile]);
  
  // Schools to display - either top N or N nearest to search location
  const displayedSchools = useMemo(() => {
    if (filteredSchools.length === 0) return [];
    
    const maxSchools = parseInt(debouncedNumSchoolsToShow) || 200;
    let schoolsToRank;
    
    if (searchLocation) {
      // Show N nearest schools to the searched location (postcode or map click)
      const schoolsWithDistance = filteredSchools.map(school => ({
        ...school,
        distance: calculateDistance(
          searchLocation.latitude, 
          searchLocation.longitude, 
          school.latitude, 
          school.longitude
        )
      })).sort((a, b) => a.distance - b.distance);
      
      schoolsToRank = schoolsWithDistance.slice(0, maxSchools);
    } else {
      // Show top N schools by percentile
      const sortedByPercentile = [...filteredSchools]
        .sort((a, b) => (b.percentile || 0) - (a.percentile || 0));
      
      schoolsToRank = sortedByPercentile.slice(0, maxSchools);
    }
    
    // Add rank based on percentile within the displayed schools
    const schoolsWithRank = [...schoolsToRank]
      .sort((a, b) => (b.percentile || 0) - (a.percentile || 0))
      .map((school, index) => ({
        ...school,
        rank: index + 1
      }));
    
    // If we were showing nearest schools, restore the distance-based order but keep the ranks
    if (searchLocation) {
      return schoolsWithRank.sort((a, b) => a.distance - b.distance);
    }
    
    return schoolsWithRank;
  }, [filteredSchools, searchLocation, debouncedNumSchoolsToShow]);
  
  // Calculate percentile range for relative coloring
  const percentileRange = useMemo(() => {
    if (displayedSchools.length === 0) return { min: 0, max: 100 };
    
    const percentiles = displayedSchools
      .map(school => school.percentile)
      .filter(p => p !== null && p !== undefined && !isNaN(p));
    
    if (percentiles.length === 0) return { min: 0, max: 100 };
    
    return {
      min: Math.min(...percentiles),
      max: Math.max(...percentiles)
    };
  }, [displayedSchools]);
  
  // Handle map click - show temporary marker
  const handleMapClick = useCallback((latlng) => {
    const newLocation = {
      latitude: latlng.lat,
      longitude: latlng.lng
    };
    setTempMarker(newLocation);
  }, []);
  
  // Confirm map location - actually set search
  const confirmMapLocation = useCallback(() => {
    if (tempMarker) {
      setSearchLocation(tempMarker);
      setMapClickLocation(tempMarker);
      setSearchType('map');
      setPostcode(''); // Clear postcode when using map search
      setTempMarker(null); // Clear temporary marker
    }
  }, [tempMarker]);
  
  // Cancel map location selection
  const cancelMapLocation = useCallback(() => {
    setTempMarker(null);
  }, []);
  
  // Handle school click in list - center map on school
  const handleSchoolClick = useCallback((school) => {
    setMapCenterLocation({
      latitude: school.latitude,
      longitude: school.longitude
    });
  }, []);
  
  // Handle map center completion
  const handleCenterComplete = useCallback(() => {
    setMapCenterLocation(null);
  }, []);
  
  // Handle postcode search
  const handlePostcodeSearch = async () => {
    if (!postcode.trim()) {
      setSearchLocation(null);
      setSearchType(null);
      setMapClickLocation(null);
      setTempMarker(null);
      return;
    }
    
    setIsSearching(true);
    try {
      // Use a geocoding service to get coordinates from postcode
      const response = await fetch(`https://api.postcodes.io/postcodes/${postcode.trim()}`);
      if (!response.ok) {
        throw new Error('Invalid postcode');
      }
      
      const data = await response.json();
      const { latitude, longitude } = data.result;
      
      setSearchLocation({ latitude, longitude });
      setSearchType('postcode');
      setMapClickLocation(null); // Clear map click when using postcode
      setTempMarker(null); // Clear temporary marker
    } catch (err) {
      setError('Error searching postcode: ' + err.message);
      setSearchLocation(null);
      setSearchType(null);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Clear all searches
  const clearSearch = () => {
    setPostcode('');
    setSearchLocation(null);
    setSearchType(null);
    setMapClickLocation(null);
    setTempMarker(null);
  };
  
  // Auto-trigger search when postcode changes
  useEffect(() => {
    if (postcode.trim()) {
      const timeoutId = setTimeout(() => {
        handlePostcodeSearch();
      }, 500); // Debounce postcode search
      
      return () => clearTimeout(timeoutId);
    } else if (searchType === 'postcode') {
      // Only clear if we're currently in postcode mode
      setSearchLocation(null);
      setSearchType(null);
      setMapClickLocation(null);
    }
  }, [postcode, searchType]);
  
  // Get display text for the search location
  const getSearchLocationText = () => {
    if (searchType === 'postcode') {
      return postcode;
    } else if (searchType === 'map') {
      return 'Selected Location';
      // return `${searchLocation.latitude.toFixed(4)}, ${searchLocation.longitude.toFixed(4)}`;
    }
    return '';
  };
  
  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Link to="/ratedtools" className={styles.backButton}>
            <span>←</span> Back to Tools
          </Link>
          <h1 className={styles.title}>UK Schools Search</h1>
          
          {/* School Type Toggle */}
          <div className={styles.schoolTypeToggle}>
            <button
              className={`${styles.toggleButton} ${schoolType === 'all' ? styles.active : ''}`}
              onClick={() => setSchoolType('all')}
            >
              All Schools
            </button>
            <button
              className={`${styles.toggleButton} ${schoolType === 'primary' ? styles.active : ''}`}
              onClick={() => setSchoolType('primary')}
            >
              Primary
            </button>
            <button
              className={`${styles.toggleButton} ${schoolType === 'secondary' ? styles.active : ''}`}
              onClick={() => setSchoolType('secondary')}
            >
              Secondary
            </button>
          </div>
        </div>
        
        {/* Filters Section */}
        <div className={styles.filtersSection}>
          <div className={styles.filterGroup}>
            <label htmlFor="minPercentile">Min Percentile:</label>
            <input
              type="number"
              id="minPercentile"
              value={minPercentile}
              onChange={(e) => setMinPercentile(e.target.value)}
              placeholder="0"
              min="0"
              max="100"
              className={styles.input}
            />
          </div>
          
          <div className={styles.filterGroup}>
            <label htmlFor="numSchoolsToShow">Number of Schools to Display:</label>
            <input
              type="number"
              id="numSchoolsToShow"
              value={numSchoolsToShow}
              onChange={(e) => setNumSchoolsToShow(e.target.value)}
              placeholder="200 (default)"
              min="1"
              max="2000"
              className={styles.input}
              title="Higher numbers may impact performance"
            />
          </div>
          
          <div className={styles.filterGroup}>
            <label htmlFor="postcode">Search by Postcode:</label>
            <div className={styles.postcodeSearch}>
              <input
                type="text"
                id="postcode"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="e.g. SW1A 1AA (or click map to place marker)"
                className={styles.input}
              />
              {(postcode.trim() || searchType === 'map') && (
                <button
                  onClick={clearSearch}
                  className={styles.clearButton}
                >
                  Clear
                </button>
              )}
            </div>
            {/* {searchType === 'map' && (
              <div className={styles.inputHelper}>
                Click location: {getSearchLocationText()}
              </div>
            )} */}
          </div>
        </div>
        
        {/* Status Info */}
        <div className={styles.statusInfo}>
          <span>
            {filtering ? 'Filtering...' : 
              searchLocation ? 
                `Showing ${displayedSchools.length} schools nearest to ${getSearchLocationText()}` :
                `Showing top ${displayedSchools.length} schools by percentile`
            }
            {schoolType !== 'all' && ` (${schoolType})`}
          </span>
          <span className={styles.totalSchools}>
            | {filteredSchools.length} schools match filters
          </span>
          {parseInt(numSchoolsToShow) > 500 && (
            <span className={styles.performanceWarning}>
              | High number selected - may impact performance
            </span>
          )}
          {!searchLocation && (
            <span className={styles.inputHelper}>
              | Click anywhere on the map to place a temporary marker | Click schools in list to center map
            </span>
          )}
        </div>
        
        {/* Loading and Error States */}
        {loading && (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <span>Loading schools data...</span>
          </div>
        )}
        
        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}
        
        {/* Main Content */}
        {!loading && !error && (
          <div className={styles.mainContent}>
            {/* Map and List Container */}
            <div className={styles.mapAndListContainer}>
              {/* Map */}
              <div className={styles.mapContainer}>
                <MapContainer
                  center={[54.5, -3]} // Center of UK
                  zoom={6}
                  className={styles.map}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  
                  <MapBoundsUpdater schools={displayedSchools} searchType={searchType} shouldUpdateBounds={!searchType} />
                  <MapClickHandler onMapClick={handleMapClick} tempMarker={tempMarker} onConfirm={confirmMapLocation} onCancel={cancelMapLocation} />
                  <MapController centerLocation={mapCenterLocation} onCenterComplete={handleCenterComplete} />
                  
                  {/* Temporary marker for map clicks */}
                  {tempMarker && (
                    <CircleMarker
                      center={[tempMarker.latitude, tempMarker.longitude]}
                      radius={10}
                      fillColor="#ff7f00"
                      color="#ffffff"
                      weight={2}
                      opacity={1}
                      fillOpacity={0.8}
                    />
                  )}
                  
                  {/* Search location marker */}
                  {searchLocation && (
                    <CircleMarker
                      center={[searchLocation.latitude, searchLocation.longitude]}
                      radius={12}
                      fillColor="#000000"
                      color="#ffffff"
                      weight={2}
                      opacity={1}
                      fillOpacity={1}
                    >
                      <Popup>
                        <div className={styles.popupContent}>
                          <h4>Search Location</h4>
                          <p>{getSearchLocationText()}</p>
                          {searchType === 'map' && (
                            <p><small>Clicked on map</small></p>
                          )}
                        </div>
                      </Popup>
                    </CircleMarker>
                  )}
                  
                  {/* Render school markers */}
                  {displayedSchools.map((school, index) => (
                    <CircleMarker
                      key={`${school.school_name}-${index}`}
                      center={[school.latitude, school.longitude]}
                      radius={8}
                      fillColor={getPercentileColor(school.percentile, percentileRange.min, percentileRange.max)}
                      color="#000000"
                      weight={1}
                      opacity={0.8}
                      fillOpacity={0.9}
                    >
                      <Popup>
                        <div className={styles.popupContent}>
                          <h4>{school.school_name}</h4>
                          <p>
                            <strong>Rank:</strong> #{school.rank} <strong>Percentile:</strong> {school.percentile || 'N/A'}
                          </p>
                          <p>
                            <strong>Type:</strong> {school.school_type.charAt(0).toUpperCase() + school.school_type.slice(1)}
                          </p>
                          {school.distance && (
                            <p>
                              <strong>Distance:</strong> {school.distance.toFixed(2)} miles
                            </p>
                          )}
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
              
              {/* Schools List */}
              <div className={styles.schoolsList}>
                <div className={styles.schoolsListHeader}>
                  <h3>
                    {searchLocation ? `Nearest Schools to ${getSearchLocationText()}` : `Top Schools by Percentile`}
                  </h3>
                  <span className={styles.schoolsCount}>
                    {displayedSchools.length} of {parseInt(numSchoolsToShow) || 200} schools
                  </span>
                </div>
                
                <div className={styles.schoolsListContent}>
                  {displayedSchools.length === 0 ? (
                    <div className={styles.noSchoolsMessage}>
                      <p>No schools match your current filters.</p>
                      <p>Try adjusting your criteria.</p>
                    </div>
                  ) : (
                    displayedSchools.map((school, index) => (
                      <div 
                        key={`${school.school_name}-${index}`} 
                        className={styles.schoolListItem}
                        onClick={() => handleSchoolClick(school)}
                      >
                        <div className={styles.schoolListItemHeader}>
                          <h4 className={styles.schoolName}>
                            #{school.rank} {school.school_name}
                          </h4>
                          <span
                            className={styles.schoolPercentileBadge}
                            style={{ backgroundColor: getPercentileColor(school.percentile, percentileRange.min, percentileRange.max) }}
                          >
                            {school.percentile ? Math.round(school.percentile) : 'N/A'}
                          </span>
                        </div>
                        <div className={styles.schoolListItemDetails}>
                          <p><strong>Type:</strong> {school.school_type.charAt(0).toUpperCase() + school.school_type.slice(1)}</p>
                          {/* <p><strong>Postcode:</strong> {school.postcode}</p> */}
                          {school.distance && (
                            <p><strong>Distance:</strong> {school.distance.toFixed(2)} miles</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Schools; 